import { useOrderValidation } from '@src/hooks';
import { useStrategies, useSubAccounts } from '@src/providers';
import type { OMSForm } from '@src/providers/OMSContext.types';
import { cleanParametersForOrder } from '@src/utils/order';
import {
  Box,
  DecisionStatusEnum,
  Flex,
  LoaderSizes,
  LoaderTalos,
  ParametersTable,
  PricingMode,
  PricingModeEnum,
  Side,
  SideEnum,
  SmartSummary,
  Table,
  Tbody,
  Td,
  Text,
  Tooltip,
  Tr,
  useSecurity,
} from '@talos/kyoko';
import { memo, useEffect } from 'react';
import { BulkOrderErrors, BulkOrderWrapper } from '../styles';
import { useOrderImportContext, type OrderErrors } from './OrderImportProvider';

export interface OrderValidationRowArgs {
  order: OMSForm;
  orderErrors: OrderErrors;
  isSending: boolean;
  enableDurationSubmission: boolean;
}

export const OrderValidationRow = memo(
  ({ order, orderErrors, isSending, enableDurationSubmission }: OrderValidationRowArgs) => {
    const { strategiesList: strategies, strategiesByName } = useStrategies();
    const security = useSecurity(order.symbol);
    const { tradableSubAccounts } = useSubAccounts();
    // Keep staged import behavior unchanged: staged orders should still submit Duration even when the flag is off.
    const enableDurationSubmissionForOrder =
      enableDurationSubmission || order.initialDecisionStatus === DecisionStatusEnum.Staged;
    const orderImportContext = useOrderImportContext();
    const { errors, warnings } = useOrderValidation({
      order: order,
      subAccounts: tradableSubAccounts,
      strategies,
      subAccountAllocations: order.subAccountAllocations || [],
      allocationValueType: order.allocationValueType || '',
      buyingPower: undefined,
      validateBuyingPower: false,
      validateMinSize: false,
      isModifying: false,
    });
    const strategy = strategiesByName.get(order.strategy || '');
    const parameters =
      strategy !== undefined
        ? cleanParametersForOrder({
            parameters: order.parameters,
            selectedStrategy: strategy,
            enableDurationSubmission: enableDurationSubmissionForOrder,
          })
        : {};

    const displayOrder = {
      AmountCurrency: security?.QuoteCurrency,
      Strategy: order.strategy,
      OrdType: order.ordType,
      Symbol: order.symbol,
      Currency: order.orderCurrency,
      Side: order.orderSide || SideEnum.Buy,
      Parameters: parameters,
      OrderQty: order.quantity,
      Price: order.price,
      DecisionStatus: order.initialDecisionStatus,
      ...(order.pricingMode === PricingMode.BPS && { PricingMode: PricingModeEnum.BPS }),
    };

    useEffect(() => {
      Object.keys(warnings).forEach(warning => {
        if (!orderErrors[warning]) {
          orderImportContext.setOrderError(order, warning, warnings[warning]);
        }
      });
      Object.keys(errors).forEach(error => {
        if (!orderErrors[error]) {
          orderImportContext.setOrderError(order, error, errors[error]);
        }
      });
    }, [errors, order, orderErrors, orderImportContext, orderImportContext.setOrderError, warnings]);

    return (
      <BulkOrderWrapper>
        <Flex flexDirection="column" gap="spacing8" flex={1}>
          <Box ml="-spacing4" mr="-spacing4">
            <Table w="100%">
              <Tbody>
                <Tr>
                  <Td colSpan={2}>
                    <Flex alignItems="center" gap="spacing4">
                      <Side side={displayOrder.Side}>{displayOrder.Side}</Side>
                      {displayOrder.Symbol}
                      {strategy && (
                        <Text color="colorTextSubtle" transform="uppercase">
                          {strategy?.DisplayName}
                        </Text>
                      )}
                      <Tooltip
                        showUnderline={true}
                        tooltip={<ParametersTable order={displayOrder} />}
                        placement="bottom"
                      >
                        <Text color="colorTextSubtle" size="fontSizeSmall">
                          Params
                        </Text>
                      </Tooltip>
                      {displayOrder.DecisionStatus === DecisionStatusEnum.Staged && (
                        <Text size="fontSizeSmall" transform="uppercase">
                          Staged
                        </Text>
                      )}
                    </Flex>
                  </Td>
                  <Td rowSpan={2}>
                    <Box flex={`0 0 ${LoaderSizes.SMALL}px`}>
                      {isSending && <LoaderTalos size={LoaderSizes.SMALL} />}
                    </Box>
                  </Td>
                </Tr>
                <Tr>
                  <Td colSpan={2}>
                    <SmartSummary entity={displayOrder} type="order" showSide={false} />
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Flex>
        <BulkOrderErrors data-testid="order-import-errors">
          {(() => {
            const keys = Object.keys(orderErrors);
            // runValidation adds the same error to every path prefix (e.g. "parameters" and "parameters.numPriceLevels");
            // only show leaf keys so each message appears once
            const leafKeys = keys.filter(
              key => !keys.some(other => other !== key && (other.startsWith(`${key}.`) || other.startsWith(`${key}[`)))
            );
            return leafKeys.map(prop => <div key={prop}>{orderErrors[prop]}</div>);
          })()}
        </BulkOrderErrors>
      </BulkOrderWrapper>
    );
  }
);
